import { Box, Switch } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Dispatch, SetStateAction } from 'react';
import moment from 'moment';
import { ViewButton } from '../../Buttons/ViewButton';
import { RefreshButton } from '../../Buttons/RefreshButton';
import { IProduct, IProductAttachment } from '../../../types/product';
import { ICategory } from '../../../types/store';

export const getCategoryColumns = (
  setIsOpenDialog: Dispatch<SetStateAction<boolean>>,
  setViewedCategory: Dispatch<SetStateAction<ICategory | null>>,
  onToggleShowInApp: (category: ICategory) => void,
): GridColDef[] => {
  return [
    {
      field: 'name',
      headerName: 'Category Name',
      flex: 1,
    },
    {
      field: 'show_inapp',
      headerName: 'Showing in App',
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <>
            <Switch
              checked={params.row.show_inapp}
              onChange={() => {
                onToggleShowInApp(params.row as ICategory);
              }}
            />
          </>
        );
      },
    },
    {
      field: 'cat',
      headerName: 'Subcategory',
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <>
            <ViewButton
              onClick={() => {
                setIsOpenDialog(true);
                setViewedCategory(params.row);
              }}
            />
          </>
        );
      },
    },
  ];
};

export const getSubcategoryColumns = (
  onUpload: (rowId: string, file: File) => void,
): GridColDef[] => {
  return [
    {
      field: 'name',
      headerName: 'Name',
      minWidth: 150,
    },
    {
      field: 'image_url',
      headerName: 'Image',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <>
            <Box
              component="img"
              src={params.row.image_url}
              alt="Superstore"
              sx={{
                height: '100%', // control logo size
                width: '100%',
                maxWidth: '100%',
                objectFit: 'contain',
              }}
            />
          </>
        );
      },
    },
    {
      field: 'toUpload',
      headerName: 'Upload',
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;

              onUpload(params.row.name, file);
            }}
          />
        );
      },
    },
  ];
};

export const getProductsColumns = (
  setViewedProduct: Dispatch<SetStateAction<IProduct | null>>,
  onForceSync: (sku: string) => void,
): GridColDef[] => {
  return [
    {
      field: 'sKU',
      headerName: 'SKU',
    },
    {
      field: 'attachments',
      headerName: 'Default Img',
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as IProduct;
        const defaultImg = row.attachments?.find((x: IProductAttachment) => x.isDefault);
        return (
          <>
            <Box
              component="img"
              src={defaultImg?.downloadUrl}
              alt="No default img"
              sx={{
                height: 80,
                width: 80,
                maxWidth: '100%',
                objectFit: 'contain',
              }}
            />
          </>
        );
      },
    },
    {
      field: 'viewAttachment',
      headerName: 'Images',
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as IProduct;
        const defaultImg = row.attachments?.find((x: IProductAttachment) => x.isDefault);
        return (
          <>
            <ViewButton
              onClick={() => {
                setViewedProduct(row);
              }}
            />
          </>
        );
      },
    },
    {
      field: 'name',
      headerName: 'Product Name',
      minWidth: 150,
    },
    {
      field: 'category',
      headerName: 'Category',
    },
    {
      field: 'additionalAttribute1',
      headerName: 'Subcategory',
    },
    {
      field: 'additionalAttribute2',
      headerName: 'Subcategory 2',
    },
    {
      field: 'priceTier1',
      headerName: 'Price 1',
    },
    {
      field: 'priceTier2',
      headerName: 'Price 2',
    },
    {
      field: 'totalstock',
      headerName: 'Total Stock',
    },
    {
      field: 'tags',
      headerName: 'Tag',
    },
    {
      field: 'updatedAt',
      headerName: 'Updated At',
      minWidth: 160,
      valueFormatter: (v: string) => (v ? moment(v).format('MMM D, YYYY HH:mm') : '—'),
    },
    {
      field: 'forceSync',
      headerName: 'Force Sync',
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as IProduct;
        return (
          <RefreshButton
            onClick={() => {
              onForceSync(row.sKU);
            }}
          />
        );
      },
    },
  ];
};
